/**
 * Phase 19 / Task 19.3 (DEC-325) — the Management dashboard (master spec §22,
 * 19-PLAN.md §1b). Tenant admins only (D-2). Every figure is for the chosen
 * period, compared with the equal-length period before it.
 *
 * Revenue is money on invoices that were SENT, by the day they were sent, with
 * voided invoices left out (D-7). Work not yet invoiced is shown separately as
 * "Unbilled" and is never added to revenue.
 */

import * as React from 'react';
import { useApiOpts } from '@lodex-pro/web-shared';
import { useUserTimezone } from '../lib/use-user-timezone';
import { formatTime } from '../lib/timestamp';
import { useManagementDashboard } from '../features/dashboard/use-management-dashboard';
import { VolumeTrendChart } from '../features/dashboard/VolumeTrendChart';
import { DashboardTabs } from '../features/dashboard/DashboardTabs';
import { DashboardExport } from '../features/dashboard/DashboardExport';
import {
  formatBbl,
  formatChange,
  formatHours,
  formatMoney,
  formatPct,
  formatRange,
  presetRange,
  type RangePreset,
} from '../features/dashboard/dashboard-format';

/** CSV choices — the API's managementTables names; the first is its default. */
const MANAGEMENT_EXPORT_TABLES = [
  { name: 'trend', label: 'Volume trend' },
  { name: 'summary', label: 'Period summary' },
  { name: 'customers', label: 'Top customers' },
  { name: 'sites', label: 'Top sites' },
  { name: 'fleet', label: 'Fleet and compliance' },
] as const;

/** A whole panel plugged into the page (DEC-325 D-5): sync health, fleet mileage, … */
interface MgmtPanel {
  readonly id: string;
  readonly render: () => React.ReactNode;
}

/** The plug-in slot for Management panels. Empty until their services ship. */
const MGMT_PANELS: readonly MgmtPanel[] = [];

const sectionStyle: React.CSSProperties = {
  borderRadius: 'var(--radius-card)',
  padding: '12px 16px',
  backgroundColor: 'var(--color-bg-secondary)',
};

const sectionTitle: React.CSSProperties = {
  margin: '0 0 10px',
  fontSize: '13px',
  fontWeight: 700,
  color: 'var(--color-text-secondary)',
};

const mutedStyle: React.CSSProperties = { fontSize: '12px', color: 'var(--color-text-tertiary)' };

const thStyle: React.CSSProperties = {
  textAlign: 'left',
  padding: '6px 8px',
  fontSize: '11px',
  fontWeight: 700,
  color: 'var(--color-text-tertiary)',
  borderBottom: '1px solid var(--color-border-secondary)',
};
const numTh: React.CSSProperties = { ...thStyle, textAlign: 'right' };
const tdStyle: React.CSSProperties = {
  padding: '6px 8px',
  fontSize: '13px',
  borderBottom: '1px solid var(--color-border-secondary)',
};
const numTd: React.CSSProperties = {
  ...tdStyle,
  textAlign: 'right',
  fontVariantNumeric: 'tabular-nums',
};

const PRESETS: readonly { id: RangePreset; label: string }[] = [
  { id: 'LAST_7', label: 'Last 7 days' },
  { id: 'LAST_30', label: 'Last 30 days' },
];

function Kpi({
  label,
  value,
  change,
  previous,
  testId,
}: {
  label: string;
  value: string;
  change: number | null;
  previous: string;
  testId: string;
}): React.JSX.Element {
  const c = formatChange(change);
  const color =
    c.direction === 'UP'
      ? 'var(--color-success)'
      : c.direction === 'DOWN'
        ? 'var(--color-danger)'
        : 'var(--color-text-tertiary)';
  return (
    <div data-testid={testId} style={{ ...sectionStyle, flex: '1 1 180px', minWidth: '180px' }}>
      <div style={mutedStyle}>{label}</div>
      <div style={{ fontSize: '24px', fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>
        {value}
      </div>
      <div style={{ fontSize: '12px' }}>
        <span style={{ color, fontWeight: 600 }}>{c.text}</span>{' '}
        <span style={mutedStyle}>vs {previous}</span>
      </div>
    </div>
  );
}

function ShareTable({
  rows,
  testId,
  nameHeader,
}: {
  rows: readonly {
    key: string;
    name: string;
    loads: number;
    bbl: string;
    sharePct: number | null;
  }[];
  testId: string;
  nameHeader: string;
}): React.JSX.Element {
  if (rows.length === 0) return <p style={mutedStyle}>No loads in this period.</p>;
  return (
    <table style={{ width: '100%', borderCollapse: 'collapse' }} data-testid={testId}>
      <thead>
        <tr>
          <th style={thStyle}>{nameHeader}</th>
          <th style={numTh}>Loads</th>
          <th style={numTh}>BBL</th>
          <th style={numTh}>Share</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((r) => (
          <tr key={r.key}>
            <td style={tdStyle}>{r.name}</td>
            <td style={numTd}>{r.loads}</td>
            <td style={numTd}>{formatBbl(r.bbl)}</td>
            <td style={numTd}>{formatPct(r.sharePct)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

export function ManagementDashboardPage(): React.JSX.Element {
  const apiOpts = useApiOpts();
  const timeZone = useUserTimezone();
  const [preset, setPreset] = React.useState<RangePreset | 'CUSTOM'>('LAST_30');
  const [range, setRange] = React.useState(() => presetRange('LAST_30', timeZone));
  const { data, isLoading, isError } = useManagementDashboard(apiOpts, range.from, range.to);

  const setCustom = (key: 'from' | 'to', value: string): void => {
    if (value === '') return;
    setPreset('CUSTOM');
    setRange((r) => ({ ...r, [key]: value }));
  };

  return (
    <div
      data-print-root
      style={{
        height: '100%',
        overflowY: 'auto',
        padding: '16px',
        display: 'flex',
        flexDirection: 'column',
        gap: '16px',
        backgroundColor: 'var(--color-bg-primary)',
        color: 'var(--color-text-primary)',
      }}
    >
      <div
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: '8px',
        }}
      >
        <DashboardTabs />
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', alignItems: 'center' }}>
          {PRESETS.map((p) => (
            <button
              key={p.id}
              type="button"
              aria-pressed={preset === p.id}
              onClick={(): void => {
                setPreset(p.id);
                setRange(presetRange(p.id, timeZone));
              }}
              style={{
                minHeight: '36px',
                padding: '0 12px',
                borderRadius: 'var(--radius-pill)',
                border: '1px solid var(--color-border-primary)',
                background:
                  preset === p.id ? 'var(--color-accent-blue)' : 'var(--color-bg-tertiary)',
                color: preset === p.id ? 'var(--color-on-accent)' : 'var(--color-text-primary)',
                cursor: 'pointer',
              }}
            >
              {p.label}
            </button>
          ))}
          <label style={mutedStyle}>
            From{' '}
            <input
              type="date"
              value={range.from}
              max={range.to}
              onChange={(e): void => setCustom('from', e.target.value)}
            />
          </label>
          <label style={mutedStyle}>
            To{' '}
            <input
              type="date"
              value={range.to}
              min={range.from}
              onChange={(e): void => setCustom('to', e.target.value)}
            />
          </label>
        </div>
      </div>
      <DashboardExport
        path="/v1/dashboards/management"
        params={{ from: range.from, to: range.to }}
        slug="management"
        tables={MANAGEMENT_EXPORT_TABLES}
      />

      {isLoading && <p style={mutedStyle}>Loading…</p>}
      {isError && (
        <p role="alert" style={{ color: 'var(--color-danger)' }}>
          The dashboard could not be loaded. It will retry automatically.
        </p>
      )}

      {data !== undefined && (
        <>
          <p style={{ ...mutedStyle, margin: 0 }} data-testid="mgmt-period">
            {formatRange(data.current.from, data.current.to)} compared with{' '}
            {formatRange(data.previous.from, data.previous.to)} · updated{' '}
            {formatTime(data.generatedAt, timeZone)}
          </p>

          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px' }}>
            <Kpi
              testId="kpi-revenue"
              label="Revenue (invoices sent)"
              value={formatMoney(data.current.revenueCents)}
              change={data.changes.revenuePct}
              previous={formatMoney(data.previous.revenueCents)}
            />
            <Kpi
              testId="kpi-loads"
              label="Loads delivered"
              value={String(data.current.loads)}
              change={data.changes.loadsPct}
              previous={String(data.previous.loads)}
            />
            <Kpi
              testId="kpi-bbl"
              label="BBL hauled"
              value={formatBbl(data.current.bbl)}
              change={data.changes.bblPct}
              previous={formatBbl(data.previous.bbl)}
            />
            <div data-testid="kpi-unbilled" style={{ ...sectionStyle, flex: '1 1 180px' }}>
              <div style={mutedStyle}>Unbilled (not yet invoiced)</div>
              <div
                style={{ fontSize: '24px', fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}
              >
                {formatMoney(data.unbilledCents)}
              </div>
              <div style={mutedStyle}>As of now · not counted as revenue</div>
            </div>
          </div>

          <section style={sectionStyle}>
            <h3 style={sectionTitle}>Volume trend (BBL per day)</h3>
            <VolumeTrendChart current={data.trend.current} previous={data.trend.previous} />
          </section>

          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px' }}>
            <section style={{ ...sectionStyle, flex: '1 1 340px' }}>
              <h3 style={sectionTitle}>Top customers by volume</h3>
              <ShareTable
                testId="top-customers"
                nameHeader="Customer"
                rows={data.topCustomers.map((c) => ({
                  key: c.haulerOperatorId ?? c.operatorId ?? c.name,
                  ...c,
                }))}
              />
            </section>
            <section style={{ ...sectionStyle, flex: '1 1 340px' }}>
              <h3 style={sectionTitle}>Top sites by volume</h3>
              <ShareTable
                testId="top-sites"
                nameHeader="Site"
                rows={data.topSites.map((s) => ({ key: s.locationId ?? s.wellId ?? s.name, ...s }))}
              />
            </section>
          </div>

          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px' }}>
            <section style={{ ...sectionStyle, flex: '1 1 340px' }} data-testid="utilization">
              <h3 style={sectionTitle}>Fleet utilization</h3>
              <p style={{ margin: '0 0 6px', fontSize: '14px' }}>
                {data.utilization.trucksUsed} of {data.utilization.trucksActive} trucks ran a shift
                ({formatPct(data.utilization.truckPct)})
              </p>
              <p style={{ margin: '0 0 6px', fontSize: '14px' }}>
                {data.utilization.driversWithLoads} of {data.utilization.driversActive} drivers
                hauled a load ({formatPct(data.utilization.driverPct)})
              </p>
              <p style={{ margin: 0, fontSize: '14px' }}>
                Average clock time per driver who hauled:{' '}
                {data.utilization.avgActiveSecsPerDriver === null
                  ? '—'
                  : formatHours(data.utilization.avgActiveSecsPerDriver)}
              </p>
            </section>
            <section style={{ ...sectionStyle, flex: '1 1 340px' }} data-testid="mgmt-compliance">
              <h3 style={sectionTitle}>Compliance</h3>
              <p style={{ margin: 0, fontSize: '14px' }}>
                {data.compliance.expired} expired · {data.compliance.expiringSoon} expiring within
                30 days
              </p>
              <p style={mutedStyle}>Details are on the Operations dashboard.</p>
            </section>
          </div>

          {MGMT_PANELS.map((p) => (
            <React.Fragment key={p.id}>{p.render()}</React.Fragment>
          ))}
        </>
      )}
    </div>
  );
}
